import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { communityTrpc } from '@org/clients/community';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  Skeleton,
  Switch,
} from '@org/ui';
import { Bell, CheckCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  NOTIFICATION_ICONS,
  NOTIFICATION_COLORS,
  NOTIFICATION_I18N_KEYS,
  getNotificationLink,
  type NotificationType,
} from './notification-presentation';

export function NotificationDropdown() {
  const { t } = useTranslation('community');
  const [isOpen, setIsOpen] = useState(false);
  const utils = communityTrpc.useUtils();

  const { data: unreadData } = communityTrpc.notifications.getUnreadCount.useQuery(
    undefined,
    { refetchInterval: 10_000 }
  );

  const { data, isLoading, refetch } = communityTrpc.notifications.list.useQuery(
    { limit: 10 },
    { enabled: isOpen, refetchInterval: 15_000 }
  );

  const { data: popupPref } = communityTrpc.preferences.getPopupPreferences.useQuery();
  const popupEnabled = popupPref?.popupEnabled ?? true;

  const setPopupPreferences = communityTrpc.preferences.setPopupPreferences.useMutation({
    onMutate: async ({ popupEnabled: next }) => {
      await utils.preferences.getPopupPreferences.cancel();
      const previous = utils.preferences.getPopupPreferences.getData();
      utils.preferences.getPopupPreferences.setData(undefined, { popupEnabled: next });
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        utils.preferences.getPopupPreferences.setData(undefined, context.previous);
      }
    },
    onSettled: () => {
      utils.preferences.getPopupPreferences.invalidate();
    },
  });

  const markAsRead = communityTrpc.notifications.markAsRead.useMutation({
    onSuccess: () => refetch(),
  });

  const markAllAsRead = communityTrpc.notifications.markAllAsRead.useMutation({
    onSuccess: () => refetch(),
  });

  const unreadCount = unreadData?.count ?? 0;
  const notifications = data?.notifications ?? [];

  const handleNotificationClick = (notificationId: string, isRead: boolean) => {
    if (!isRead) {
      markAsRead.mutate({ notificationId });
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-2">
          <DropdownMenuLabel>{t('notifications.title')}</DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
            >
              <CheckCheck className="mr-1 h-3 w-3" />
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <DropdownMenuSeparator />

        <div className="max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2 p-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <Bell className="mx-auto h-8 w-8 opacity-50" />
              <p className="mt-2 text-sm">{t('notifications.empty')}</p>
            </div>
          ) : (
            <div className="py-1">
              {notifications.map((notification) => {
                const type = notification.type as NotificationType;
                const Icon = NOTIFICATION_ICONS[type] || Bell;
                const color = NOTIFICATION_COLORS[type] || 'text-muted-foreground';
                const data = notification.payload as Record<string, unknown> | null;
                const actorName = (data?.actorName as string | undefined) ?? undefined;
                const postTitle = (data?.postTitle as string | undefined) ?? undefined;
                const badge = (data?.badgeName as string | undefined) ?? undefined;
                const level = (data?.level as number | undefined) ?? undefined;
                const link = getNotificationLink(type, data);

                return (
                  <Link
                    key={notification.notificationId}
                    to={link}
                    onClick={() =>
                      handleNotificationClick(
                        notification.notificationId,
                        notification.read
                      )
                    }
                    className={`flex items-start gap-3 px-3 py-2 transition-colors hover:bg-muted/50 ${
                      !notification.read ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className={`mt-0.5 ${color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm">
                        {actorName && <span className="font-medium">{actorName} </span>}
                        {t(NOTIFICATION_I18N_KEYS[type] ?? 'notifications.newNotification', { badge, level })}
                      </p>
                      {postTitle && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          "{postTitle}"
                        </p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <DropdownMenuSeparator />
        <div className="flex items-center justify-between px-3 py-2">
          <label htmlFor="popup-alerts-toggle" className="text-sm">
            {t('notifications.popupAlerts')}
          </label>
          <Switch
            id="popup-alerts-toggle"
            checked={popupEnabled}
            onCheckedChange={(checked) =>
              setPopupPreferences.mutate({ popupEnabled: checked })
            }
          />
        </div>

        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Link
                to="/notifications"
                className="block w-full rounded-md bg-muted/50 py-2 text-center text-sm font-medium text-primary hover:bg-muted"
              >
                {t('notifications.viewAll')}
              </Link>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
